import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './Footer';
import Nav from './Nav';

function Terms() {
  return (
    <>
      <Nav />
      <Helmet>
        <title>Terms of Service | Speak Your Menu</title>
        <meta name="description" content="Read the terms of service for Speak Your Menu, governing the use of our platform and services." />
        <link rel="canonical" href="https://speakyourmenu.com/terms" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
            { "@type": "ListItem", position: 2, name: "Terms of Service", item: "https://speakyourmenu.com/terms" },
          ],
        })}</script>
      </Helmet>
      <main id="main-content" tabIndex="-1" className="container my-5">
        <h1 className="mb-4">Terms of Service</h1>
        <p className="text-muted">Last Updated: September 16, 2025</p>

        <section>
          <h2>1. Services</h2>
          <h3>1.1 Description</h3>
          <p>Speak Your Menu is a menu-based training platform that enables restaurants to train their staff through structured, interactive, gamified lessons about food, wine, ingredients, allergens, and service communication.</p>
          <h3>1.2 Modifications</h3>
          <p>We may update, enhance, or discontinue any part of the Services at our sole discretion, with or without notice, subject to applicable law. Continued use of the Services after changes constitutes acceptance.</p>
        </section>

        <section>
          <h2>2. Account Registration and Usage</h2>
          <h3>2.1 Account Registration</h3>
          <p>You agree to provide accurate, current, and complete information when registering for an account and to keep your information updated. You are responsible for all activity on your account.</p>
          <h3>2.2 User Roles</h3>
          <p>Customers may register as Managers, Directors, or Employees. Access and functionality vary by role. You agree to access the Services only in your assigned role and not to impersonate others.</p>
          <h3>2.3 Eligibility</h3>
          <p>You must be at least 16 years old to use the Services. Users under 18 must do so with the supervision of a parent, legal guardian, or employer representative. We do not knowingly collect personal data from children under 16.</p>
          <h3>2.4 Acceptable Use</h3>
          <p>You agree not to misuse the Services, interfere with normal operation, or use the Services for unlawful, fraudulent, infringing, or harmful purposes.</p>
        </section>

        <section>
          <h2>3. Fees, Billing & Free Trials</h2>
          <h3>3.1 Subscription Plans</h3>
          <p>Access to the Services may require payment of a subscription fee. Details of current plans and pricing are available on our website.</p>
          <h3>3.2 Free Trials</h3>
          <p>We may offer a free-trial period. If not canceled before the trial ends, you authorize us to charge your payment method for the applicable plan.</p>
          <h3>3.3 Billing and Renewals</h3>
          <p>Subscriptions renew automatically at the end of each billing period. You authorize recurring billing unless you cancel your subscription before the renewal date.</p>
          <h3>3.4 Refund Policy</h3>
          <p>Except where required by law, all payments are non-refundable. You are responsible for canceling your subscription before the next billing cycle.</p>
        </section>

        <section>
          <h2>4. Data Collection &amp; Privacy</h2>
          <ol className="ms-3">
            <li>
              <strong>4.1 Data Collection</strong>
              <p className="ms-3">We collect personal, usage, and business data through your use of the Services. Details are outlined in our <a href="/privacy">Privacy Policy</a>.</p>
            </li>
            <li>
              <strong>4.2 Data Usage</strong>
              <p className="ms-3">We use collected data to operate, improve, and personalize our Services. We may also use data for product development, research, and marketing.</p>
            </li>
            <li>
              <strong>4.3 Data Sale</strong>
              <p className="ms-3">We may aggregate and anonymize data to sell or share with third parties for industry analysis or marketing purposes. We do not sell personally identifiable information without your consent, except as allowed under applicable laws.</p>
            </li>
            <li>
              <strong>4.4 Security Measures</strong>
              <ul className="ms-4">
                <li>Encryption in transit and at rest using TLS 1.2/1.3 and AES-256 at rest.</li>
                <li>Hardened infrastructure on AWS EC2 with ISO 27001, SOC 2, and PCI-DSS controls.</li>
                <li>Role-based access and MFA for production systems.</li>
                <li>Regular vulnerability scans, dependency monitoring, and annual penetration testing.</li>
                <li>Daily encrypted backups in AWS S3 with lifecycle policies and documented breach-response procedures.</li>
              </ul>
            </li>
            <li>
              <strong>4.5 Data Processing Addendum</strong>
              <p className="ms-3">Where required by applicable law, the processing of Personal Data is further governed by our Data Processing Addendum (DPA), available at <a href="https://speakyourmenu.com/dpa" target="_blank" rel="noopener noreferrer">https://speakyourmenu.com/dpa</a> and incorporated into these Terms by reference.</p>
            </li>
            <li>
              <strong>4.6 Third-Party Sub-processors</strong>
              <p className="ms-3">We engage carefully selected third-party service providers (“Sub-processors”) to help operate the Platform. Current Sub-processors include:</p>
              <ul className="ms-4">
                <li>AWS – application hosting (EC2), object storage (S3), and encrypted backups</li>
                <li>MongoDB Atlas – managed database clusters and automated snapshots</li>
                <li>Stripe – payment processing for subscription fees</li>
                <li>Google (Gmail SMTP) – outbound transactional and onboarding email delivery</li>
                <li>MailerLite – marketing email campaigns for our public landing page</li>
              </ul>
              <p className="ms-3">We will provide at least 10 days’ advance notice before adding or replacing a Sub-processor so you may reasonably object on privacy grounds. An up-to-date list is maintained at <a href="https://speakyourmenu.com/subprocessors" target="_blank" rel="noopener noreferrer">https://speakyourmenu.com/subprocessors</a>.</p>
            </li>
          </ol>
        </section>

        <section>
          <h2>5. Intellectual Property</h2>
          <h3>5.1 Our Property</h3>
          <p>All intellectual property related to the Services (including design, code, text, software, and trademarks) is owned by Speak Your Menu, Inc. or its licensors. No ownership rights are transferred to you.</p>
          <h3>5.2 Customer Content</h3>
          <p>Any content you submit remains your property. By uploading content, you grant us a non-exclusive, royalty-free, worldwide licence to use it as necessary to provide and improve the Services.</p>
        </section>

        <section>
          <h2>6. Disclaimers</h2>
          <p>THE SERVICES ARE PROVIDED “AS IS” WITHOUT WARRANTY OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE.</p>
        </section>

        {/* 🚨 NEW SECTION ADDED HERE */}
        <section>
          <h2>6A. Assessment Results Disclaimer</h2>
          <p>
            Speak Your Menu may provide access to assessment tools that estimate potential
            performance improvements (“lift”) across various categories. These results are
            generated from your inputs and published research but are for informational
            purposes only and do not constitute guarantees of future performance.
          </p>
          <p>
            Actual outcomes depend on factors beyond our control, including staff
            participation, baseline knowledge, implementation fidelity, menu complexity,
            and external conditions. Speak Your Menu disclaims all liability for decisions
            made or actions taken based on assessment results. By using the assessment,
            you acknowledge that the projections are estimates only and agree to review the
            full <a href="/disclaimer">Disclaimer</a>.
          </p>
        </section>
        {/* 🚨 END OF NEW SECTION */}

        <section>
          <h2>7. Limitation of Liability</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, SPEAK YOUR MENU, INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRIOR TO THE CLAIM.</p>
        </section>

        <section>
          <h2>8. Indemnification</h2>
          <p>You agree to indemnify and hold harmless Speak Your Menu, Inc., its affiliates, officers, employees, and agents from any claims, damages, liabilities, and expenses (including attorney fees) arising out of your use of the Services or violation of this Agreement.</p>
        </section>

        {/* ... remaining sections unchanged ... */}

        <section>
          <h2>Acknowledgment</h2>
          <p>By using our Services, you acknowledge that you have read, understood, and agreed to be bound by this Terms of Service.</p>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default Terms;
